import { Button, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { FluentIcon } from '@fluentui/react-icons';
import { FC, MouseEvent } from 'react';

import { ControlledTooltip } from '@shared/components';
import { AI_EDIT_REVIEW_LOCK_REASON } from '@modules/documents/components/DocumentControlsLock';
import useEditorActive from '@modules/documents/hooks/useEditorActive.hook';
import { isAiEditReviewActive } from '@modules/documents/tiptap/plugins/AiEditReviewPlugin';

type TIconButtonProps = {
  /** The icon to display. */
  Icon: FluentIcon;

  /** Whether the button is selected and has to be highlighted. */
  isSelected: boolean;

  /** The click handler for the button. */
  onClick?: (e: MouseEvent) => void;
};

const useStyles = makeStyles({
  button: {
    // Reset all default styles
    margin: 0,
    padding: 0,
    minWidth: 0,
    minHeight: 0,
    height: 'fit-content',
    width: 'fit-content',
    border: 'none',
    background: 'none',
    cursor: 'pointer',

    '&:hover': {
      color: tokens.colorBrandForeground1,
    },

    '&:disabled': {
      cursor: 'not-allowed',
    },

    '&:active': {
      scale: 0.95,
    },
  },
  buttonSelected: {
    color: tokens.colorBrandForeground1,
  },
});

/**
 * A reusable Fluent UI button that shows a comment icon plus optional label.
 * Used by both SingleIcon and CombinedIcon.
 */
const IconButton: FC<TIconButtonProps> = ({ Icon, isSelected, onClick }) => {
  const classes = useStyles();
  const isAiEditLocked = useEditorActive((editor) => isAiEditReviewActive(editor.state), false);

  const selectedClassName = isSelected ? classes.buttonSelected : '';

  return (
    <ControlledTooltip
      relationship="inaccessible"
      enable={isAiEditLocked}
      content={AI_EDIT_REVIEW_LOCK_REASON}
      withArrow={true}
      positioning="before"
    >
      <Button
        disabled={isAiEditLocked}
        onClick={onClick}
        icon={<Icon fontSize="18px" className={selectedClassName} />}
        appearance="transparent"
        size="small"
        className={mergeClasses(classes.button, selectedClassName)}
      />
    </ControlledTooltip>
  );
};

export default IconButton;
